// Package Imports
import { Form } from 'antd';
import { useTranslate } from '@refinedev/core';
import { useEffect } from 'react';
import { SaveButton } from '@refinedev/antd';
import { useWatch } from 'antd/es/form/Form';
import { useRulesCreateState } from 'pages/rules/context/RulesCreateStateContext';
// Project Imports
import { initialValues } from 'pages/rules/RulesDummyData';
import useRulesDropdown from 'pages/rules/RulesDropdown';
import { getDmnOutputVariableList } from 'pages/rules/ruleApiResponse';
import ruleDetailsItem from 'pages/rules/ruleDetailItem';
import checkFormulaValidation from 'pages/rules/utils/checkFormulaValidation';
import { useLocation, useParams } from 'react-router-dom';
import RulesFormContent from 'pages/rules/RulesFormContent';

const RulesForm = ({
  formProps,
  form,
  saveButtonProps,
  onFinishHandler,
  unifiedListUnfiltered,
  ruleGroupDate,
  ruleType,
  saveLoading,
  type: actualPageType,
}) => {
  const state = useRulesCreateState();
  const t = useTranslate();
  const { fileType } = useRulesDropdown();
  const { id } = useParams();
  const location = useLocation();
  const hasShow = location.pathname.toLowerCase().includes('show');
  const disabled = false; // Set as needed or get from state
  const isFileDataEmpty = state.inputSourceFileData === undefined;
  const clawbackType = useWatch('clawback_type', { form, preserve: true });
  const defaultFileListObj = isFileDataEmpty
    ? []
    : [
        {
          uid: Date.now().toString(),
          name:
            state.inputSourceFileData?.file_name ||
            state.inputSourceFileData?.name,
          status:
            state.inputSourceFileData?.file_upload_status === 'true' && 'done',
        },
      ];
  useEffect(() => {
    state.setSelectedFileType(
      formProps?.initialValues?.results?.rule_file_type
    );
  }, [formProps?.initialValues]);
  const isValuesPresent = formProps?.initialValues?.results || initialValues;
  const ruleGroupId = useWatch('rule_group', { form, preserve: true });
  // prettier-ignore
  const ruleGroupConfigurationStatus = useWatch('rule_group_configuration_status', { form, preserve: true });
  const { dmnOutputList } = getDmnOutputVariableList(ruleGroupId, id, hasShow);
  const groupConfigFields = [
    ...(dmnOutputList?.length > 0
      ? dmnOutputList.map((element) => ({
          ...element,
          data_type: 'number',
          columnType: 'normal',
          name: element?.label,
          dataType:
            element?.data_type === 'string' ? 'text' : element?.data_type,
        }))
      : []),
    ...(state.dmnData?.outputFields?.length
      ? state.dmnData.outputFields.map((item) => {
          let newType;
          let dataType;
          if (item?.type === 'double') {
            newType = 'number';
            dataType = 'number';
          } else if (item?.type === 'string') {
            newType = 'text';
            dataType = 'text';
          } else {
            newType = item?.type;
            dataType = item?.type;
          }
          return {
            label: item?.name,
            value: item?.name,
            data_type: newType,
            columnType: 'normal',
            name: item?.name,
            dataType,
          };
        })
      : []),
    {
      label: 'Final Payout',
      value: 'Final Payout',
      data_type: 'number',
      columnType: 'normal',
      name: 'Final Payout',
      dataType: 'number',
    },
  ];
  const getOutputVarsForDmnList = (outputFields: any[] = []) =>
    outputFields.map((item) => {
      let newtype: string;
      let dataType;
      if (item?.type === 'double') {
        newtype = 'number';
        dataType = 'number';
      } else if (item?.type === 'string') {
        newtype = 'text';
        dataType = 'text';
      } else {
        newtype = item?.type;
        dataType = item?.type;
      }
      return {
        label: item?.name,
        data_type: newtype,
        dataType,
        name: item?.name,
        value: item?.name,
      };
    });
  const getUpdatedDmnList = () =>
    dmnOutputList.map((element) => ({ ...element, data_type: 'number' }));
  const getUniList = () =>
    unifiedListUnfiltered.map((item) => ({
      label: item?.label,
      value: item?.value,
      data_type: item?.data_type,
    }));
  useEffect(() => {
    const uniList = unifiedListUnfiltered ? getUniList() : [];
    const updatedDmnList =
      dmnOutputList && dmnOutputList.length > 0 ? getUpdatedDmnList() : [];
    const outVarsForDmnList =
      state.dmnData?.outputFields && state.dmnData.outputFields.length > 0
        ? getOutputVarsForDmnList(state.dmnData.outputFields)
        : [];
    let list: any[] = [];
    if (uniList && ruleType) {
      const hasUpdatedDmnList = updatedDmnList.length > 0;
      const hasDmnOutputFields = outVarsForDmnList.length > 0;
      if (hasUpdatedDmnList && hasDmnOutputFields) {
        list = [...uniList, ...updatedDmnList, ...outVarsForDmnList];
      } else if (hasDmnOutputFields) {
        list = [...uniList, ...outVarsForDmnList];
      } else if (hasUpdatedDmnList) {
        list = [...uniList, ...updatedDmnList];
      } else {
        list = [...uniList];
      }
    }
    if ((!uniList || !ruleType) && updatedDmnList.length > 0) {
      list = [...updatedDmnList];
    }
    const seen = new Set();
    const uniqueList = list.filter((item) => {
      const key = item?.value ?? item?.label;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    state.setConcatList(uniqueList);
    if (uniqueList?.length > 0) {
      const updatedConfigData = uniqueList.map((item) => {
        let dataType;
        if (item?.data_type === 'string') {
          dataType = 'text';
        } else if (item?.data_type === 'double') {
          dataType = 'number';
        } else {
          dataType = item?.data_type;
        }
        return { ...item, columnType: 'normal', name: item?.label, dataType };
      });
      const seenConfig = new Set();
      const uniqueConfigFields = updatedConfigData.filter((item) => {
        const key = item?.name ?? item?.label;
        if (seenConfig.has(key)) return false;
        seenConfig.add(key);
        return true;
      });
      state.setConfigFields(uniqueConfigFields);
    }
    // prettier-ignore
  }, [
    unifiedListUnfiltered,
    ruleType,
    state.dmnData?.outputFields,
    dmnOutputList,
  ]);

  useEffect(() => {
    state.setHasInvalidOutputColumn(false);
    state.setHasInvalidInputColumn(false);
  }, [state.dmnData?.outputFields, state.dmnData?.inputFields]);

  const uploadType = useWatch('file_upload_type', { form, preserve: true });
  // prettier-ignore
  const selectedAdjustmentLevel = useWatch('adjustment_level', { form, preserve: true, });
  const outputFormulaEditorType = useWatch('output_formula_editor_type', {
    preserve: true,
    form,
  });
  const showFeelExpression = ruleType !== 'clawback';

  const showRuleApplicability =
    ruleType !== 'clawback' &&
    state.configFields &&
    selectedAdjustmentLevel !== 'group';

  const isClawbackDirect =
    ruleType === 'clawback' &&
    (clawbackType === 'RECALCULATION' || clawbackType === 'FULL');
  const isClawbackNotSelected = ruleType === 'clawback' && !clawbackType;
  const isButtonDisabled =
    isClawbackDirect || isClawbackNotSelected
      ? false // bypass validation for RECALCULATION& reverse
      : checkFormulaValidation(state, outputFormulaEditorType);
  console.log(
    clawbackType,
    showFeelExpression,
    isClawbackNotSelected,
    state.configFields,
    'debug'
  );

  const type = state.pageType;
  const renderContent = () => (
    <RulesFormContent
      type={type}
      form={form}
      fileType={fileType}
      defaultFileListObj={defaultFileListObj}
      uploadType={uploadType}
      selectedAdjustmentLevel={selectedAdjustmentLevel}
      groupConfigFields={groupConfigFields}
      state={state}
      ruleGroupConfigurationStatus={ruleGroupConfigurationStatus}
      showRuleApplicability={showRuleApplicability}
      showFeelExpression={showFeelExpression}
      isClawbackDirect={isClawbackDirect}
      isClawbackNotSelected={isClawbackNotSelected}
      clawbackType={clawbackType}
    />
  );
  return (
    <Form
      {...formProps}
      form={form}
      initialValues={isValuesPresent}
      onFinish={onFinishHandler}
      disabled={type === 'Show' || disabled}
      layout="vertical"
    >
      {ruleDetailsItem({
        form,
        type,
        ruleGroupDate,
        disabled,
        t,
        ruleType,
      })}
      {ruleGroupId ? <div>{renderContent()}</div> : null}
      {type !== 'Show' && actualPageType !== 'Show' ? (
        <div className="flex justify-end mt-6">
          <SaveButton
            icon={false}
            {...saveButtonProps}
            disabled={isButtonDisabled}
            loading={saveLoading}
            className="min-w-[100px]"
          >
            {t('buttons.saveRule')}
          </SaveButton>
        </div>
      ) : null}
    </Form>
  );
};
export default RulesForm;
